import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { ModalDialogContainer } from '@common/modal'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LoadingButton,
  MenuItem,
  TextInput,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { useModal } from '@common/GlobalModal'
import { useToast } from '@core/toast'
import { localizedStrings } from '@core/strings'
import { AttachmentInput } from '@elements/Questions/AttachmentInput'
import { AttachmentTypes } from '@core/components/templateAnswersForm'
import { useAddRegisterItemAttachments } from '@core/react-query/features/registers/registers/register/items/item'
import { SiteBoxDocument } from '@core/entities/SiteBoxDocument'
import { Text } from '@fundamentals'

type AddAttachmentsToRegisterItemModalProps = {
  registerId: number
  registerItemId: number
  organisationId: number
  projectId?: number
  attachmentProperties: Array<{
    propertyId: string
    propertyName: string
  }>
}

type PickedFile = File & {
  uri?: string
  key?: string
  siteBoxDocument?: SiteBoxDocument
}

type FormSchema = {
  propertyId: string | undefined
  files: PickedFile[]
}

export const AddAttachmentsToRegisterItemModal: React.FC<
  AddAttachmentsToRegisterItemModalProps
> = ({ registerId, registerItemId, projectId, attachmentProperties }) => {
  const { hideModal } = useModal()
  const toast = useToast()

  const hasSingleProperty = attachmentProperties.length === 1
  const defaultPropertyId = hasSingleProperty
    ? attachmentProperties[0].propertyId
    : undefined

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitted },
  } = useForm<FormSchema>({
    defaultValues: {
      propertyId: defaultPropertyId,
      files: [],
    },
  })

  const addAttachmentsMutation = useAddRegisterItemAttachments()

  const onSubmit = ({ propertyId, files }: FormSchema) => {
    addAttachmentsMutation.mutate(
      {
        registerId,
        registerItemId,
        propertyId,
        files: files.filter((file) => !file.siteBoxDocument),
        siteboxPicks: files
          .filter((file) => file.siteBoxDocument?.id)
          .map((file) => ({ siteBoxDocumentId: file.siteBoxDocument.id })),
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.attachmentsAddedSuccessfully)
          hideModal()
        },
        onError: (error) => toast.error(error.message),
      },
    )
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.addAttachments}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!hasSingleProperty && (
          <Controller
            name='propertyId'
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextInput
                sx={{ mb: 2, mt: 1 }}
                label={localizedStrings.property}
                select
                fullWidth
                error={!!fieldState.error}
                {...field}
              >
                {attachmentProperties.map((attachmentProperty) => (
                  <MenuItem
                    key={attachmentProperty.propertyId}
                    value={attachmentProperty.propertyId}
                  >
                    {attachmentProperty.propertyName}
                  </MenuItem>
                ))}
              </TextInput>
            )}
          />
        )}

        <Controller
          name='files'
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <AttachmentInput
              borderColor={fieldState.error && 'error.main'}
              attachmentTypes={[
                AttachmentTypes.PDF,
                AttachmentTypes.IMAGE,
                AttachmentTypes.WORD,
                AttachmentTypes.EXCEL,
                AttachmentTypes.CAD,
                AttachmentTypes.VIDEO,
              ]}
              attachmentLayout='list'
              showFileNames
              enableLinkSiteBoxDocuments={!!projectId}
              projectId={projectId}
              inputProps={{
                value: field.value,
                onChange: (proofs) => {
                  field.onChange(proofs)
                  field.onBlur()
                },
              }}
            />
          )}
        />
        {!!Object.keys(errors).length && isSubmitted && (
          <Text variant='body2' color='error' mt={2}>
            {localizedStrings.pleaseFillAllFieldsCorrrectly}
          </Text>
        )}
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='outlined' onClick={hideModal}>
          {localizedStrings.cancel}
        </Button>
        <LoadingButton
          variant='contained'
          onClick={handleSubmit(onSubmit)}
          disabled={addAttachmentsMutation.isLoading}
          loading={addAttachmentsMutation.isLoading}
        >
          {localizedStrings.addAttachment}
        </LoadingButton>
      </DialogActions>
    </ModalDialogContainer>
  )
}
